import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {addBreadcrumb} from '@sentry/browser';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {STORAGE_KYC_TERMS_MODAL_DISMISSED} from '@/app/auth/constants/KycConstants';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {useLoading} from '@/app/zustand/useLoading';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';

import {
  KYC_SOURCE_OF_FUNDS_OPTION_IDS,
  KycSourceOfFundsOptionId,
} from './constants';
import {useTrackers} from './trackers';

// Type guard so a backend source-of-funds value only pre-selects an option when
// it matches one of the ids rendered on this screen.
const isKycSourceOfFundsOptionId = (
  value: string | undefined,
): value is KycSourceOfFundsOptionId =>
  !!value &&
  KYC_SOURCE_OF_FUNDS_OPTION_IDS.includes(value as KycSourceOfFundsOptionId);

// Terms & Conditions are shown on this screen until the user acknowledges them
// once. Kept as a standalone function so the lazy state initializer stays small.
const getInitialShowTermsModal = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  return localStorage.getItem(STORAGE_KYC_TERMS_MODAL_DISMISSED) !== 'true';
};

type ViewModelContextType = {
  selectedOption: KycSourceOfFundsOptionId | null;
  isShowTermsAndConditionsModal: boolean;
  disableButton: boolean;
  handleSelectOption: (optionId: KycSourceOfFundsOptionId) => void;
  handleCloseTermsAndConditions: () => void;
  handleOnClickContinue: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {startLoading, stopLoading} = useLoading();
  const {profile, updateSourceOfFunds} = useUsersRepository();
  const {trackSelectSourceOfFunds} = useTrackers();

  // The selected source of funds is kept in local state for now.
  // It will be wired to the KYC API in a follow-up.
  const [selectedOption, setSelectedOption] =
    useState<KycSourceOfFundsOptionId | null>(null);

  // Pre-select the option already stored on the parent's profile (if any) once
  // it loads, so returning users see their previous choice. We only seed the
  // initial state — later user selections must not be overwritten by the
  // profile, so this runs only while nothing is selected.
  useEffect(() => {
    if (selectedOption) return;
    if (isKycSourceOfFundsOptionId(profile?.sourceOfFunds)) {
      setSelectedOption(profile.sourceOfFunds);
    }
  }, [profile?.sourceOfFunds, selectedOption]);

  // Terms & Conditions are presented on this screen (before the address step).
  // The modal opens automatically until the user acknowledges it once.
  const [isShowTermsAndConditionsModal, setIsShowTermsAndConditionsModal] =
    useState<boolean>(getInitialShowTermsModal);

  const handleCloseTermsAndConditions = useCallback(() => {
    localStorage.setItem(STORAGE_KYC_TERMS_MODAL_DISMISSED, 'true');
    setIsShowTermsAndConditionsModal(false);
    addBreadcrumb({
      level: 'info',
      category: 'onboarding.kyc.sourceOfFunds.handleCloseTermsAndConditions',
      type: 'user',
      message: 'Accepted terms and conditions',
    });
  }, []);

  const handleSelectOption = useCallback(
    (optionId: KycSourceOfFundsOptionId) => {
      setSelectedOption(optionId);
      addBreadcrumb({
        level: 'info',
        category: 'onboarding.kyc.sourceOfFunds.handleSelectOption',
        type: 'user',
        message: `Selected source of funds: ${optionId}`,
      });
    },
    [],
  );

  const handleOnClickContinue = useCallback(async () => {
    if (!selectedOption) return;

    trackSelectSourceOfFunds(selectedOption);

    addBreadcrumb({
      level: 'info',
      category: 'onboarding.kyc.sourceOfFunds.handleOnClickContinue',
      type: 'user',
      message: `Continue with source of funds: ${selectedOption}`,
    });

    startLoading();
    try {
      // Called once on this screen; persists the parent's source of funds
      // before moving on to the home address step.
      await updateSourceOfFunds(selectedOption);
    } catch {
      // The error is already logged in the repository. Surface it to the user
      // and stay on this screen so they can retry.
      showSnackbar({
        summary: t('kyc.source_of_funds.error.summary'),
        message: t('kyc.source_of_funds.error.message'),
      });
      return;
    } finally {
      stopLoading();
    }

    navigate('/onboarding/kyc/not-started-address');
  }, [
    navigate,
    selectedOption,
    startLoading,
    stopLoading,
    t,
    trackSelectSourceOfFunds,
    updateSourceOfFunds,
  ]);

  const disableButton = useMemo(() => !selectedOption, [selectedOption]);

  const value = useMemo(
    () => ({
      selectedOption,
      isShowTermsAndConditionsModal,
      disableButton,
      handleSelectOption,
      handleCloseTermsAndConditions,
      handleOnClickContinue,
    }),
    [
      selectedOption,
      isShowTermsAndConditionsModal,
      disableButton,
      handleSelectOption,
      handleCloseTermsAndConditions,
      handleOnClickContinue,
    ],
  );

  return (
    <ViewModelContext.Provider value={value}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error(
      'KycSourceOfFunds useViewModelProvider must be used within a ViewModelProvider',
    );
  }
  return context;
}
